import React, { startTransition } from "react";
import {
  Button,
  ButtonClear,
  ButtonExpansion,
  Text,
  useIsMobile,
  useMediaQuery,
} from "@salestrak/ui";

import { EntityTag, type EntityTagProps } from "#app/components/entity";
import { useAppData } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

const BUTTON_LABEL = "Only Me";

type CurrentUserButtonSize = "xs" | "sm" | "default" | "lg";

type CurrentUserButtonProps = Omittable<{
  selected: boolean;
  onClick: () => void;
  onClearClick?: () => void;
  size?: CurrentUserButtonSize;
  /** Pin the compact (avatar-only) face — never expand to the "Only Me" pill.
   *  For tight chrome like a widget card header where the pill would overrun. */
  compact?: boolean;
}>;

// Cap-filling sizes for the pill's flush face: the avatar circle should
// nearly fill the pill's inner height (e.g. xs → 20px avatar in a 24px pill).
const AVATAR_SIZES: Record<
  CurrentUserButtonSize,
  NonNullable<EntityTagProps["size"]>
> = {
  xs: "xs",
  sm: "xs",
  default: "md",
  lg: "md",
};

// The xs chip lands on `sm` so the avatar face keeps the same footprint as
// the sibling toolbar chips.
const BUTTON_SIZES: Record<CurrentUserButtonSize, "sm" | "default" | "lg"> = {
  xs: "sm",
  sm: "sm",
  default: "default",
  lg: "lg",
};

export const CurrentUserButton = React.memo(function CurrentUserButton({
  selected,
  onClick,
  onClearClick,
  size = "default",
  compact = false,
}: CurrentUserButtonProps) {
  const { currentUser } = useAppData();
  const isMobile = useIsMobile();
  const isCoarsePointer = useMediaQuery({ pointer: "coarse" });
  const isDesktop = !isMobile && !isCoarsePointer;
  const [visualSelected, setVisualSelected] = React.useState(selected);

  React.useEffect(() => {
    setVisualSelected(selected);
  }, [selected]);

  const handlePress = React.useCallback(() => {
    // Desktop resolves to "on" because the clear × in the expanded pill is
    // the off switch — except in compact mode, which never expands, so the
    // press itself must toggle (as it does on touch).
    const nextSelected = isDesktop && !compact ? true : !selected;
    setVisualSelected(nextSelected);

    startTransition(() => {
      onClick();
    });
  }, [compact, isDesktop, onClick, selected]);

  const handleClear = React.useCallback(() => {
    setVisualSelected(false);

    startTransition(() => {
      onClearClick?.();
    });
  }, [onClearClick]);

  const avatar = (
    <EntityTag
      entity={{
        kind: "user",
        id: currentUser?.userId ?? "me",
        user: currentUser ?? null,
      }}
      display="visual"
      size={AVATAR_SIZES[size]}
      interactive={false}
    />
  );

  return (
    <Button
      size={BUTTON_SIZES[size]}
      variant={visualSelected ? "primary-outline" : "ghost"}
      aria-pressed={visualSelected}
      expanded={visualSelected && !compact}
      onClick={handlePress}
      aria-label="Filter to my projects"
      shape="pill"
    >
      {avatar}
      <ButtonExpansion>
        <Text size="sm" weight="medium" truncate>
          {BUTTON_LABEL}
        </Text>
      </ButtonExpansion>
      {visualSelected && onClearClick ? (
        <ButtonClear
          aria-label="Clear current user filter"
          onClick={handleClear}
        />
      ) : null}
    </Button>
  );
});

CurrentUserButton.displayName = "CurrentUserButton";
